import { MouseEvent, useCallback, useContext, useState } from 'react';
import { Link as LinkIcon } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { OPM_CALCULATOR_ENABLER } from 'common/constants/features';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { SHOW_OPM_CALCULATIONS_TEST_ID, SHOW_OPM_CALCULATIONS_TOOLTIP } from './constants';
import { ShowOPMCalculationsLinkProps } from './ShowOPMCalculationsLink.props';

/**
 * Opens the OPM Calculator prefilled from an OPM input surface (Equity Allocation, Backsolve,
 * Future Exit).
 *
 * Not `cell.link` / `withLink`: that HOC navigates to a fixed `url`, and this has to resolve or
 * create the target calculator panel first.
 */
const ShowOPMCalculationsLink = ({
  getInputs,
  sourceLabel,
  sourceLink,
  revealOnHover = false,
  className,
}: ShowOPMCalculationsLinkProps) => {
  const [, checkFeature] = useCheckFeatures();
  const { openOPMCalculator } = useContext(ValuationContext);
  // Also revealed on focus: a keyboard user cannot hover, and an invisible focused button is
  // worse than one that appears.
  const [isRevealed, setIsRevealed] = useState(false);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      openOPMCalculator?.(getInputs(), sourceLabel, sourceLink);
    },
    [getInputs, openOPMCalculator, sourceLabel, sourceLink]
  );

  if (!checkFeature(OPM_CALCULATOR_ENABLER) || !openOPMCalculator) {
    return null;
  }

  return (
    <Box
      className={className}
      onBlur={() => setIsRevealed(false)}
      onFocus={() => setIsRevealed(true)}
      onMouseEnter={() => setIsRevealed(true)}
      onMouseLeave={() => setIsRevealed(false)}
      sx={
        // In a cell the wrapper fills it, so hovering anywhere in the cell reveals the icon.
        // Standalone it is just an inline button.
        revealOnHover
          ? { alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center', width: '100%' }
          : { display: 'inline-flex' }
      }>
      <Tooltip title={SHOW_OPM_CALCULATIONS_TOOLTIP} placement="top" arrow>
        <IconButton
          aria-label={SHOW_OPM_CALCULATIONS_TOOLTIP}
          data-testid={SHOW_OPM_CALCULATIONS_TEST_ID}
          // The cell owns mouse-down for selection; without this the click only selects the cell.
          onMouseDown={event => event.stopPropagation()}
          onClick={handleClick}
          size="small"
          sx={{
            opacity: revealOnHover && !isRevealed ? 0 : 1,
            transition: 'ease opacity 0.2s',
          }}>
          <LinkIcon fontSize="small" color="secondary" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ShowOPMCalculationsLink;
